import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import AGGridWrapper from 'components/AGGrid/AGGridWrapper';
import HelperFunctions from 'utils/HelperFunctions';
import { createColumnDef } from 'utils/agGridDefaults';
import * as Constants from "utils/Constants";

const MasterDetailGrid = React.forwardRef(({
  manageCampaignSummary,
  manageCampaignDetail,
  searchText,
  includeNoRevenue,
  filterSummary,
  filterDetails,
  sortValue,
  isESPRole,
  campaignSelection,
  handleSummaryMenueDisplay,
  menuSetting,
  setMenuSetting,
  detailColumnDefs,
  setDetailColumnDefs,
}, ref) => {
  const [gridApi, setGridApi] = useState(null);

  // Callback to handle when detail grid columns are changed
  const handleDetailColumnsChange = (newColumns) => {
    if (setDetailColumnDefs) {
      setDetailColumnDefs(newColumns);
    }
  };

  // Expand/Collapse All functions
  const expandAll = () => {
    if (gridApi) {
      try {
        // For master/detail grids, use forEachNode to expand each master row
        gridApi.forEachNode((node) => {
          if (node.master) {
            node.setExpanded(true);
          }
        });
      } catch (error) {
        // Expand all not supported in this AG Grid version
      }
    }
  };

  const collapseAll = () => {
    if (gridApi) {
      try {
        // For master/detail grids, use forEachNode to collapse each master row
        gridApi.forEachNode((node) => {
          if (node.master) {
            node.setExpanded(false);
          }
        });
      } catch (error) {
        // Collapse all not supported in this AG Grid version
      }
    }
  };

  // Expose methods to parent component
  React.useImperativeHandle(ref, () => ({
    expandAll,
    collapseAll,
    refreshDetailGrids: () => {
      // Force re-render of all detail grids by refreshing the master grid
      if (gridApi) {
        gridApi.forEachNode((node) => {
          if (node.expanded) {
            node.setExpanded(false);
            setTimeout(() => node.setExpanded(true), 100);
          }
        });
      }
    }
  }));

  // Detail Cell Renderer for AG Grid Enterprise Master/Detail
  const DetailCellRenderer = (props) => {
    if (!props || !props.data) {
      return <div style={{ padding: '20px', backgroundColor: '#f0f0f0' }}>No data available</div>;
    }

    const masterData = props.data;

    // Filter detail data for this master row
    let detailData;

    if (masterData.marketingPlayId === null || masterData.uncategorized === true) {
      // For the synthetic uncategorized summary, get campaigns with uncategorized === 1 or no marketingPlayId
      detailData = manageCampaignDetail?.filter(detail =>

        (detail.uncategorized === 1 || !detail.marketingPlayId) &&

        (filterDetails == null || (detail.status?.toLowerCase() === filterDetails.filterText.toLowerCase() && filterDetails.filterType === 2)) &&
        (!searchText || detail.name?.toLowerCase().includes(searchText.toLowerCase())) &&
        (includeNoRevenue || detail.revenue > 0)
      ) || [];
    } else {
    
      // For categorized campaigns, match by marketingPlayId
      detailData = manageCampaignDetail?.filter(detail =>

        detail.marketingPlayId === masterData.marketingPlayId &&
        (filterDetails == null ||
          (detail.status?.toLowerCase() === filterDetails.filterText.toLowerCase() &&
            filterDetails.filterType === 2)) &&
        (!searchText || detail.name?.toLowerCase().includes(searchText.toLowerCase())) &&
        (includeNoRevenue || detail.revenue > 0)
      ) || [];
    }

    // Always show the grid with columns, even if no data
    return (
      <div style={{ height: '400px', width: '100%', padding: '10px' }}>
        <div style={{ height: '100%', width: '100%' }}>
          <AGGridWrapper
            columnDefs={detailColumnDefs}
            rowData={detailData} // This can be empty array, grid will show "No Rows To Show"
            pagination={true}
            paginationPageSize={10}
            rowHeight={45}
            headerHeight={40}
            preset="standard"
            enableManageColumns={true}
            clientId={HelperFunctions.getCookie("selectedClientId")}
            disabledFields={["", "name"]}
            entityName={Constants.MANAGE_COLUMN_ENTITIES.ManageCampaigns}
            enableCellTextSelection={true}
            domLayout="normal"
            gridOptions={{
              defaultColDef: {
                cellStyle: {
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
              }
            }}
          />
        </div>
      </div>
    );
  };

  // Master row data preparation 
  const masterRowData = useMemo(() => {
    if (!manageCampaignSummary) return [];

    // Process all campaigns from the summary
    const filteredData = manageCampaignSummary
      .filter(summary => {
        const summaryFilterPassed = filterSummary == null ||
          (summary.playName?.toLowerCase() === filterSummary.filterText.toLowerCase() &&
            filterSummary.filterType === 1);

        // Show uncategorized if it has campaigns, regardless of revenue filter
        // For other campaigns, respect the revenue filter
        const revenueFilterPassed = summary.hasUncategorizedCampaigns ||
          (includeNoRevenue || summary.revenue > 0);

        // Additional check: if there's a search term, also include master rows that have matching detail rows
        let hasMatchingDetailRows = false;
        if (searchText && manageCampaignDetail) {
          if (summary.marketingPlayId === null || summary.uncategorized === true) {
            // For uncategorized campaigns
            hasMatchingDetailRows = manageCampaignDetail.some(detail =>
              (detail.uncategorized === 1 || !detail.marketingPlayId) &&
              detail.name?.toLowerCase().includes(searchText.toLowerCase()) &&
              (includeNoRevenue || detail.revenue > 0)
            );
          } else {
            // For categorized campaigns
            hasMatchingDetailRows = manageCampaignDetail.some(detail =>
              detail.marketingPlayId === summary.marketingPlayId &&
              detail.name?.toLowerCase().includes(searchText.toLowerCase()) &&
              (includeNoRevenue || detail.revenue > 0)
            );
          }
        }

        // Check if master row itself matches the search term
        const masterRowMatchesSearch = !searchText || 
          summary.playName?.toLowerCase().includes(searchText.toLowerCase());

        // Include master row if:
        // 1. It passes normal filters AND (no search OR master row matches search)
        // 2. OR it has matching detail rows (regardless of master row name)
        return (summaryFilterPassed && revenueFilterPassed && masterRowMatchesSearch) || hasMatchingDetailRows;
      })
      .map((summary, index) => ({
        ...summary,
        // Add a unique ID for AG Grid to track rows properly
        id: summary.marketingPlayId || `uncategorized-${index}`,
        // Add a flag to identify this as master row data
        isMasterRow: true
      }));

    // Separate uncategorized and categorized rows
    const uncategorizedRows = filteredData.filter(row => row.uncategorized === true || row.marketingPlayId === null);
    const categorizedRows = filteredData.filter(row => row.uncategorized !== true && row.marketingPlayId !== null);

    // Always put uncategorized first, then categorized rows sorted by revenue descending
    // Take only the first uncategorized row to avoid duplicates
    const finalData = [];
    if (uncategorizedRows.length > 0) {
      finalData.push(uncategorizedRows[0]); // Only take the first uncategorized
    }

    // Sort categorized rows by revenue descending by default
    const sortedCategorizedRows = categorizedRows.sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
    finalData.push(...sortedCategorizedRows);

    return finalData;
  }, [manageCampaignSummary, manageCampaignDetail, filterSummary, includeNoRevenue, searchText]);

  // Master column definitions
  const masterColumnDefs = useMemo(() => {
    const columns = [
      {
        // This is the expand/collapse column for master/detail
        field: 'expand',
        headerName: '',
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          innerRenderer: () => '', // Empty inner renderer since we just want the expand/collapse button
        },
        pinned: 'left',
        suppressMovable: true,
        suppressMenu: true,
        suppressSorting: true,
        suppressFilter: true,
        flex: 0 // Don't flex this column
      },
      {
        headerName: "Campaign Name",
        field: "playName",
        sortable: true,
        resizable: true,
        flex: 2, // Take up more space
        minWidth: 300,
        cellClass: "campaign-name-cell",
        cellRenderer: (params) => {
          const obj = params.data;
          if (!obj) return null;

          return (
            <div className={`d-flex campaign-info`}>
              <div className={`icon ${obj.uncategorized ? 'uncategorized' : obj.playCssClass || ''}`}>
                <i className={obj.uncategorized ? 'fa-regular fa-file-lines m-auto' : `${obj.playIcon} m-auto`} />
              </div>
              <div className="titles ml-2">
                <div className="secondary-title">
                  {obj.playName || 'Uncategorized'}
                  {obj.playSubtitle && obj.playSubtitle.toLowerCase() !== 'uncategorized' && (
                    <span> - <strong>{obj.playSubtitle}</strong></span>
                  )}
                </div>
              </div>
            </div>
          );
        },
        comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
          // Always keep uncategorized at the top
          const aIsUncategorized = nodeA.data.uncategorized === true || nodeA.data.marketingPlayId === null;
          const bIsUncategorized = nodeB.data.uncategorized === true || nodeB.data.marketingPlayId === null;

          if (aIsUncategorized && !bIsUncategorized) return -1;
          if (!aIsUncategorized && bIsUncategorized) return 1;

          // Both are same type, sort normally by name
          const nameA = (valueA || '').toLowerCase();
          const nameB = (valueB || '').toLowerCase();
          return nameA.localeCompare(nameB);
        },
        suppressMovable: true,
        pinned: 'left'
      },
      createColumnDef({
        headerName: "Total Revenue",
        field: "revenue",
        fieldType: "number",
        width: 100,
        flex: 1, // Flexible width
        cellClass: 'total-revenue-cell ag-right-aligned-cell pr-2',
        cellRenderer: (params) => {
          if (!params.data) return null;
          return (
            <div className="revenue-master-cell">
              {HelperFunctions.formateNumberbyCommas(params.data.revenue, true, 0, true)}
            </div>
          );
        },
        comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
          // Always keep uncategorized at the top
          const aIsUncategorized = nodeA.data.uncategorized === true || nodeA.data.marketingPlayId === null;
          const bIsUncategorized = nodeB.data.uncategorized === true || nodeB.data.marketingPlayId === null;

          if (aIsUncategorized && !bIsUncategorized) return -1;
          if (!aIsUncategorized && bIsUncategorized) return 1;

          // Both are same type, sort normally by revenue
          return valueA - valueB;
        },
        suppressMovable: true
      })
    ];

    // Always add action column (for both ESP and non-ESP roles)
    columns.push({
      headerName: "Action",
      field: "action",
      width: 120,
      maxWidth: 120,
      minWidth: 120,
      flex: 0, // Don't flex this column
      cellRenderer: (params) => {
        const obj = params.data;
        if (!obj) return null;

        // Get the current isESPRole value from the component props/context
        // instead of relying on closure to avoid stale closure issues
        const currentIsESPRole = params.context?.isESPRole ?? isESPRole;

        if (currentIsESPRole) {
          const marketingPlayId = obj.marketingPlayId || 0;
          return (
            <div id="action-wrap">
              <select
                className="browser-default actionSelect form-control"
                onChange={(e) => {
                  if (e.target.value === "addEmail") {
                    campaignSelection(marketingPlayId);
                    e.target.value = ""; // Reset selection
                  }
                }}
                value=""
              >
                <option value="" title="" disabled>
                  &#xf142;
                </option>
                <option value="addEmail" title="Add and Send Email">
                  Add and Send Email
                </option>
              </select>
            </div>
          );
        } else {
          return <div></div>; // Empty cell for non-ESP users
        }
      },
      suppressMovable: true,
      pinned: 'right'
    });

    return columns;
  }, [isESPRole, handleSummaryMenueDisplay, campaignSelection, menuSetting, setMenuSetting]);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  // Apply sorting when sortValue changes
  useEffect(() => {
    if (gridApi && sortValue) {
      const [attribute, direction] = sortValue.split('.');
      const sort = direction === 'd' ? 'desc' : 'asc';

      if (attribute === 'revenue') {
        gridApi.applyColumnState({
          state: [{ colId: 'revenue', sort }],
          defaultState: { sort: null }
        });
      } else if (attribute === 'playName') {
        gridApi.applyColumnState({
          state: [{ colId: 'playName', sort }],
          defaultState: { sort: null }
        });
      }
    }
  }, [gridApi, sortValue]);



  return (
    <div style={{ height: 'calc(100vh - 400px)', width: '100%' }}>
      <AGGridWrapper
        columnDefs={masterColumnDefs}
        rowData={masterRowData}
        onGridReady={onGridReady}
        rowHeight={45} // Master row height as specified
        enableCellTextSelection={true}
        // Master/Detail props passed directly to AgGridReact
        masterDetail={true}
        detailCellRenderer={DetailCellRenderer}
        detailRowHeight={400}
        detailRowAutoHeight={true}
        isRowMaster={(dataItem) => {
          return true; // All summary rows should expand
        }}
        getRowId={(params) => params.data.id} // Help AG Grid identify rows

        gridOptions={{
          // Pass current isESPRole through context to cell renderers
          context: {
            isESPRole: isESPRole
          },

          // Disable all right-click functionality except sorting for master table
          getContextMenuItems: (params) => {
            if (params.node && params.node.master) {
              // For master rows, only allow sorting
              return ['sortAscending', 'sortDescending'];
            }
            // For detail rows, allow all context menu items
            return 'default';
          },

          defaultColDef: {
            cellStyle: {
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            flex: 1, // Make columns flexible to fill width
            minWidth: 100
          },

          // Make grid fill available space
          domLayout: 'normal',

          // Enable text filter on all columns
          enableFilter: true
        }}
      />
    </div>
  );
});

export default MasterDetailGrid;